<script setup lang="ts">
  import { computed, inject, watch } from 'vue'
  import { accessCodes, testIds } from '@gat-ship/types'
  import { useAgencyContext } from '../composables/useAgency'
  import { Button, ContentRowTwoColumns } from '@gat-ship/vue-components'
  import { DataContainerAddress } from '@gat-ship/ref-data'
  import { IconGroup, IconAgencyStructure } from '@gat-ship/icons'
  import { LinkPhone, LinkEmail } from '@gat-ship/vue-components'
  import { useUser } from '@gat-ship/user'
  import { agencyModalsKey } from '../components'

  const user = useUser()

  const props = defineProps<{ id: string }>()

  const agencyProvider = useAgencyContext()
  const agencyModals = inject(agencyModalsKey)

  watch(
    () => props.id,
    async (agencyId) => {
      if (agencyId) {
        await agencyProvider?.fetchAgency(agencyId)
      }
    },
    { immediate: true }
  )

  const agency = computed(() => agencyProvider?.agency.value)
</script>
<template>
  <ContentRowTwoColumns
    title="Contact Info"
    description="The agency's official contact details."
  >
    <div class="flex flex-col gap-2 pb-2">
      <DataContainerAddress
        :address-test-id="testIds.agency.details.contactAddress"
        :country-name-test-id="testIds.agency.details.contactCountryName"
        :show-icons="true"
        :data="{
          address: agency?.contactInfo?.address,
          countryCode: agency?.contactInfo?.country.code
        }"
      />
      <LinkPhone
        :phone-number="agency?.contactInfo?.phoneNumber"
        :show-icon="true"
        :test-id="testIds.agency.details.contactPhoneNumber"
      />
      <LinkPhone
        :phone-number="agency?.contactInfo?.mobilePhoneNumber"
        :test-id="testIds.agency.details.contactMobilePhone"
        :show-icon="true"
      />
      <LinkEmail
        :email="agency?.contactInfo?.email"
        :test-id="testIds.agency.details.contactEmail"
        :show-icon="true"
      />
    </div>
    <Button
      v-if="
        agency &&
        user.hasAgencyPermissions(
          accessCodes.administration.agency.changeContactInfo,
          agency.id
        ) &&
        agency.canChangeContactInfo
      "
      variant="outline"
      size="sm"
      class="w-fit"
      :data-testid="testIds.agency.action.changeAgencyContactInfo"
      @click="agencyModals?.showChangeAgencyContactInfoModal({ agency })"
    >
      Change Contact Info
    </Button>
  </ContentRowTwoColumns>
  <ContentRowTwoColumns
    title="Agency Group"
    description="The agency group this agency belongs to."
  >
    <div class="flex flex-col">
      <div class="flex items-center gap-2">
        <icon-group class="size-4" />
        <div class="py-2" :data-testid="testIds.agency.details.parentGroupName">
          {{ agency?.parentGroup?.name }}
        </div>
      </div>
    </div>
    <Button
      v-if="agency && user.isTenantAdmin.value && agency.canMove"
      variant="outline"
      size="sm"
      class="w-fit"
      :data-testid="testIds.agency.action.moveAgencyNode"
      @click="agencyModals?.showMoveAgencyModal({ agency })"
    >
      Move Agency
    </Button>
  </ContentRowTwoColumns>
  <ContentRowTwoColumns
    title="Linked Company"
    description="The company this agency is legally bound to."
  >
    <div class="flex flex-col">
      <div class="flex items-center gap-2">
        <icon-agency-structure class="size-4" />
        <div
          class="py-2"
          :data-testid="testIds.agency.details.linkedCompanyName"
        >
          {{ agency?.linkedCompany?.name }}
        </div>
      </div>
    </div>
    <Button
      v-if="agency && user.isTenantAdmin.value && agency.canLinkCompany"
      variant="outline"
      size="sm"
      class="w-fit"
      :data-testid="testIds.agency.action.linkToOtherCompany"
      @click="agencyModals?.showLinkToOtherCompanyModal({ agency })"
    >
      Change Linked Company
    </Button>
  </ContentRowTwoColumns>
</template>
